import React from "react";
import { DeleteOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useCartDrawer } from "context/CartDrawerContext";
import {
  getCartItemLineTotal,
  getCartItemUnitPrice,
  getCartSubtotal,
} from "utils/cartPricing";
import {
  CartItem,
  CheckoutButton,
  CloseButton,
  ColumnHeaders,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  EmptyState,
  FooterNote,
  ItemDetails,
  ItemImage,
  ItemImagePlaceholder,
  ItemName,
  ItemPrice,
  ItemsList,
  LineTotal,
  QuantityButton,
  QuantityControl,
  QuantityRow,
  QuantityValue,
  RemoveButton,
  StyledDrawer,
  SubtotalLabel,
  SubtotalRow,
  SubtotalValue,
} from "./styles";

const CartDrawer = () => {
  const navigate = useNavigate();
  const {
    isOpen,
    cartItems,
    closeDrawer,
    removeFromCart,
    updateCartItemQuantity,
  } = useCartDrawer();

  const subtotal = getCartSubtotal(cartItems);

  const handleCheckout = () => {
    closeDrawer();
    navigate("/checkout");
  };

  return (
    <StyledDrawer
      title={null}
      placement="right"
      width={420}
      onClose={closeDrawer}
      open={isOpen}
      closable={false}
      destroyOnClose={false}
    >
      <DrawerContent>
        <DrawerHeader>
          <DrawerTitle>Your cart</DrawerTitle>
          <CloseButton type="button" aria-label="Close cart" onClick={closeDrawer}>
            ×
          </CloseButton>
        </DrawerHeader>

        {cartItems.length > 0 && (
          <ColumnHeaders>
            <span>Product</span>
            <span>Total</span>
          </ColumnHeaders>
        )}

        <ItemsList>
          {!cartItems.length ? (
            <EmptyState>Your cart is empty.</EmptyState>
          ) : (
            cartItems.map((item, index) => {
              const unitPrice = getCartItemUnitPrice(item);
              const lineTotal = getCartItemLineTotal(item);
              const itemKey = `${item.productId}-${item.variantId || "base"}-${index}`;

              return (
                <CartItem key={itemKey}>
                  {item.productImage ? (
                    <ItemImage src={item.productImage} alt={item.productName || "Product"} />
                  ) : (
                    <ItemImagePlaceholder>No image</ItemImagePlaceholder>
                  )}

                  <ItemDetails>
                    <ItemName>{item.productName}</ItemName>
                    <ItemPrice>${unitPrice.toFixed(2)}</ItemPrice>
                    <QuantityRow>
                      <QuantityControl>
                        <QuantityButton
                          type="button"
                          aria-label="Decrease quantity"
                          disabled={item.productQuantity <= 1}
                          onClick={() =>
                            updateCartItemQuantity(index, item.productQuantity - 1)
                          }
                        >
                          −
                        </QuantityButton>
                        <QuantityValue>{item.productQuantity}</QuantityValue>
                        <QuantityButton
                          type="button"
                          aria-label="Increase quantity"
                          onClick={() =>
                            updateCartItemQuantity(index, item.productQuantity + 1)
                          }
                        >
                          +
                        </QuantityButton>
                      </QuantityControl>
                      <RemoveButton
                        type="button"
                        aria-label="Remove item"
                        onClick={() => removeFromCart(index)}
                      >
                        <DeleteOutlined />
                      </RemoveButton>
                    </QuantityRow>
                  </ItemDetails>

                  <LineTotal>${lineTotal.toFixed(2)}</LineTotal>
                </CartItem>
              );
            })
          )}
        </ItemsList>

        <DrawerFooter>
          <SubtotalRow>
            <SubtotalLabel>Subtotal</SubtotalLabel>
            <SubtotalValue>${subtotal.toFixed(2)} USD</SubtotalValue>
          </SubtotalRow>
          <FooterNote>Taxes and shipping calculated at checkout</FooterNote>
          <CheckoutButton
            type="button"
            disabled={!cartItems.length}
            onClick={handleCheckout}
          >
            Check out
          </CheckoutButton>
        </DrawerFooter>
      </DrawerContent>
    </StyledDrawer>
  );
};

export default CartDrawer;
